import React, { createContext, useContext, useState } from "react";
/// <reference types="web-bluetooth" />

interface BluetoothContextType {
  device: BluetoothDevice | null;
  connectToBluetooth: () => Promise<void>;
  sendData: (values: string | string[], buttonNum: number) => Promise<void>;
  startSetup: () => Promise<void>;
  configureChannels: (channels: string[]) => Promise<void>;
  configureVolume: (min: number, max: number) => Promise<void>;
  renameRemote: (newName: string) => Promise<void>;
  checkTestComplete: () => Promise<boolean>;
  sendSignalResponse: (response: string) => Promise<void>;
  findRemote: (playSound: string) => Promise<void>;
}

const BluetoothContext = createContext<BluetoothContextType | undefined>(
  undefined
);

export const BluetoothProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [device, setDevice] = useState<BluetoothDevice | null>(null);
  const [characteristics, setCharacteristics] = useState<{
    [key: number]: BluetoothRemoteGATTCharacteristic | undefined;
  }>({});

  const bleService = "19b10000-e8f2-537e-4f6c-d104768a1214";
  const uuids = {
    buttonSetup: "3412d6f5-180c-4db2-bdcd-0bf60f894bbb",
    volumeConfig: "6270e6ad-efd2-428a-9344-925f0f083aaa",
    faveChannels: "9693efbe-c8d8-4414-9565-064b32202fff",
    result: "a4581910-503d-46e9-bd6d-98dabfb65ef6",
    renameRemote: "d79f1904-e0d5-4a7b-bae8-869b2962fddd",
    findRemote: "9693efbe-c8d8-4414-9565-064b32202ccc",
  };

  const connectToBluetooth = async () => {
    try {
      const selectedDevice = await navigator.bluetooth.requestDevice({
        filters: [{ namePrefix: "BMERIT" }],
        optionalServices: [bleService],
      });
      setDevice(selectedDevice);

      const server = await selectedDevice.gatt?.connect();
      const service = await server?.getPrimaryService(bleService);
   console.log("got here1");
      const buttonSetupChar = await service?.getCharacteristic(uuids.buttonSetup);
      const volumeConfigChar = await service?.getCharacteristic(uuids.volumeConfig);
      const faveChannelsChar = await service?.getCharacteristic(uuids.faveChannels);
      const resultChar = await service?.getCharacteristic(uuids.result);
      const renameChar = await service?.getCharacteristic(uuids.renameRemote);
      const findRemoteChar = await service?.getCharacteristic(uuids.findRemote);
   console.log("got here2");
      setCharacteristics({
        1: faveChannelsChar,
        2: volumeConfigChar,
        3: buttonSetupChar,
        4: renameChar,
        5: findRemoteChar,
      });
   console.log("got here3");
    console.log(resultChar);
      if (resultChar) {
        console.log(resultChar);
        resultChar.addEventListener("characteristicvaluechanged", handleCharacteristicChange);
        await resultChar.startNotifications();
      }
    } catch (error) {
      console.error("Failed to connect to Bluetooth device:", error);
    }
  };

  const handleCharacteristicChange = (event: Event) => {
        console.log("got here4");

    const characteristic = event.target as BluetoothRemoteGATTCharacteristic;
    const value = characteristic.value;
    const decodedValue = new TextDecoder().decode(value);
    console.log("Characteristic value changed:", decodedValue);
  };

  const sendData = async (values: string | string[], buttonNum: number) => {
    const targetCharacteristic = characteristics[buttonNum];
    if (!targetCharacteristic) {
      console.error("Invalid button number or characteristic not available");
      return;
    }

    try {
      const data = new TextEncoder().encode(
        Array.isArray(values) ? values.join(",") : values
      );
      await targetCharacteristic.writeValue(data);
      console.log(`Data sent to characteristic: ${targetCharacteristic.uuid}`);
    } catch (error) {
      console.error("Error sending data:", error);
    }
  };

  const startSetup = async () => {
    try {
      await sendData("setup", 3);
      console.log("Device setup complete");
    } catch (error) {
      console.error("Error during device setup:", error);
    }
  };

  const configureChannels = async (channels: string[]) => {
    try {
      await sendData(channels, 1);
      console.log("Channels configured:", channels);
    } catch (error) {
      console.error("Error configuring channels:", error);
    }
  };

  const configureVolume = async (min: number, max: number) => {
    try {
      await sendData(`${min}-${max}`, 2);
      console.log(`Volume configured: min=${min}, max=${max}`);
    } catch (error) {
      console.error("Error configuring volume:", error);
    }
  };

  const renameRemote = async (newName: string) => {
    try {
      await sendData(newName, 4);
      console.log("Remote renamed to:", newName);
    } catch (error) {
      console.error("Error renaming remote:", error);
    }
  };

  const sendSignalResponse = async (response: string) => {
    try {
      await sendData(response, 3);
      console.log("Signal response sent:", response);
    } catch (error) {
      console.error("Error sending signal response:", error);
    }
  };

  const checkTestComplete = async () => {
    try {
      const targetCharacteristic = characteristics[3];
      if (!targetCharacteristic) {
        console.error("Characteristic for user test not available");
        return false;
      }
      const value = await targetCharacteristic.readValue();
      const decodedValue = new TextDecoder().decode(value);
      const signalReceived = "signalReceived";

      return decodedValue === signalReceived;
    } catch (error) {
      console.error("Error during user test:", error);
      return false;
    }
  };

  const findRemote = async (playSound: string) => {
    try {
      await sendData(playSound, 5);
      console.log("Find remote action:", playSound);
    } catch (error) {
      console.error("Error finding remote:", error);
    }
  };

  return (
    <BluetoothContext.Provider
      value={{
        device,
        connectToBluetooth,
        sendData,
        startSetup,
        configureChannels,
        configureVolume,
        renameRemote,
        checkTestComplete,
        sendSignalResponse,
        findRemote,
      }}
    >
      {children}
    </BluetoothContext.Provider>
  );
};

export const useBluetooth = () => {
  const context = useContext(BluetoothContext);
  if (!context) {
    throw new Error("useBluetooth must be used within a BluetoothProvider");
  }
  return context;
};
